/* eslint-disable no-console */
import { Client, IMessage } from '@stomp/stompjs';

import { formatTopicId, formatTopicPath } from '@webtrader/core/format/socket';
import storage, { EStorageKey } from '@webtrader/core/storage';

import { ISocketTopic } from './index';

export enum ESymbolMessageType {
  LIVE = 'LIVE'
}

export interface ISymbolLivePrices {
  liveLow: number;
  liveHigh: number;
  liveBuy: number;
  liveSell: number;
  liveSpread: number;
}

export interface ISymbolTopicMessageData {
  A: number; // ask
  B: number; // bid
  L: number; // low
  H: number; // high
  T: number; // time
  S: string; // symbol
}

export interface ISymbolTopicMessage {
  M: ESymbolMessageType;
  D: ISymbolTopicMessageData;
}

export interface ISymbolTopicReceiver {
  (payload: ISymbolTopicMessage | undefined): void;
}

interface ISubscribeParams {
  symbol: string;
  receiver: ISymbolTopicReceiver;
}

interface IProps {
  client: Client;
}

class SymbolTopic implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<ISymbolTopicReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { symbol, receiver } = params;

    const { subPath, topicId } = this.mapTopicIds(symbol);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      this.receivers[subPath]?.add(receiver);
    } else {
      this.receivers[subPath] = new Set([receiver]);

      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { symbol, receiver } = params;

    const { subPath, topicId } = this.mapTopicIds(symbol);

    if (!this.client) {
      return;
    }

    this.receivers[subPath]?.delete(receiver);

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  private onReceive = (id: string, message: IMessage) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as ISymbolTopicMessage;

    receivers.forEach((receiver) => receiver(payload));
  };

  private mapTopicIds = (symbol: string) => {
    const marketSubscription = storage.getItem<string>(EStorageKey.MARKET_SUBSCRIPTION);
    const subPath = `symbol/${marketSubscription}/${symbol}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default SymbolTopic;
